import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { tradePackagesAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import SignInBlock from '../../shell/SignInBlock';
import MagicCodeEntry from '../MagicCodeEntry';

/**
 * /login — shell-native magic-link sign-in.
 * Two flows, same as always:
 *  - normal: request a magic link (stashes loginReturnUrl for VerifyMagicLink)
 *  - pending package: a guest-built package waits in localStorage; the email
 *    submits it directly via tradePackagesAPI.create
 * Signed-in users are redirected to returnUrl (with returnState) or /home.
 */
const TradePackagesLogin = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [sentMode, setSentMode] = useState(null); // null | 'link' | 'package'
  const [sentEmail, setSentEmail] = useState('');
  const [hasPendingPackage, setHasPendingPackage] = useState(false);

  const { returnUrl, returnState } = location.state || {};

  useEffect(() => {
    // If user is already authenticated, redirect them
    if (isAuthenticated) {
      if (returnUrl) {
        navigate(returnUrl, { state: returnState });
      } else {
        navigate('/home');
      }
    }
  }, [isAuthenticated, returnUrl, returnState, navigate]);

  useEffect(() => {
    // Check if there's a pending package to submit
    const pendingTrades = localStorage.getItem('pendingPackageTrades');
    const pendingContext = localStorage.getItem('pendingPackageContext');
    if (pendingTrades && pendingContext) {
      setHasPendingPackage(true);
    }
  }, []);

  const handleReset = () => {
    setSentMode(null);
    setSentEmail('');
  };

  const handleSubmit = async (email) => {
    try {
      // Check if this is a package submission flow
      const pendingTrades = localStorage.getItem('pendingPackageTrades');
      const pendingContext = localStorage.getItem('pendingPackageContext');

      if (pendingTrades && pendingContext) {
        // Submit the package directly with the entered email
        const trades = JSON.parse(pendingTrades);
        const context = JSON.parse(pendingContext);

        const formattedTrades = trades.map((trade) => ({
          creatorItems: trade.yourSide.map((player) => {
            if (player.position === 'PICK') {
              return { type: 'pick', pickData: player.pickData || {}, name: player.name };
            }
            return { type: 'player', playerId: player.id, name: player.name, position: player.position, team: player.team };
          }),
          recipientItems: trade.theirSide.map((player) => {
            if (player.position === 'PICK') {
              return { type: 'pick', pickData: player.pickData || {}, name: player.name };
            }
            return { type: 'player', playerId: player.id, name: player.name, position: player.position, team: player.team };
          }),
        }));

        const payload = {
          email: email,
          leagueId: context.leagueId,
          recipientSleeperUserId: context.recipientId,
          recipientDisplayName: context.opponentOwner?.display_name || 'Team Owner',
          creatorSleeperUserId: context.userRosterId?.toString() || null,
          creatorSleeperUsername: context.userSleeperUsername,
          creatorDisplayName: email,
          trades: formattedTrades,
        };

        await tradePackagesAPI.create(payload);

        // Clear the pending package data
        localStorage.removeItem('pendingPackageTrades');
        localStorage.removeItem('pendingPackageContext');

        setSentMode('package');
      } else {
        // Normal magic link flow
        // Store returnUrl in localStorage so VerifyMagicLink can redirect after verification
        if (returnUrl) {
          localStorage.setItem('loginReturnUrl', returnUrl);
        }

        await api.post('/auth/request-magic-link', {
          email,
          returnUrl: returnUrl || '/home',
          returnState,
        });

        setSentMode('link');
      }
    } catch (error) {
      console.error('Error:', error);
      throw error; // SignInBlock renders the message inline
    }
  };

  const sent = sentMode !== null;

  const hero = sent
    ? 'Check your email.'
    : hasPendingPackage
      ? 'One email and it sends.'
      : 'Magic link, no password.';

  const sub = sent
    ? sentMode === 'package'
      ? `Package created and sent to ${sentEmail}. The link inside opens your trade package — check spam if it hides.`
      : `We sent a link to ${sentEmail}. It expires in 15 minutes — check spam if it hides.`
    : hasPendingPackage
      ? 'Enter your email and we create your trade package and send it to your inbox.'
      : 'Enter your email and the link signs you in.';

  return (
    <div className="ft-view">
      <div className="ft-homehead">
        <span className="ft-k brand">Fantasy Taco</span>
        <h1 className="ft-hero">{hero}</h1>
        <p className="ft-sub" style={{ maxWidth: 520 }}>{sub}</p>
      </div>

      {sent ? (
        <div style={{ maxWidth: 480 }}>
          {sentMode === 'link' ? (
            <MagicCodeEntry email={sentEmail} redirectTo={returnUrl || '/home'} />
          ) : null}
          <button className="ft-btn ghost" style={{ marginTop: 18 }} onClick={handleReset}>
            Use a different email
          </button>
        </div>
      ) : (
        <div style={{ maxWidth: 480 }}>
          <SignInBlock
            onSubmit={handleSubmit}
            onSent={setSentEmail}
            buttonLabel={hasPendingPackage ? 'Create package' : 'Send the link'}
            sendingLabel={hasPendingPackage ? 'Creating package…' : 'Sending…'}
            placeholder="your@email.com"
            errorFallback="Failed to send email. Please try again."
          />
        </div>
      )}
    </div>
  );
};

export default TradePackagesLogin;
